// Page Questionnaire Q2 - Management — Mode Focus
// 1 question = 1 écran, barre unifiée en haut, auto-advance
// Plus de panneau latéral, plus de boutons prev/next
// Flux wizard avec themes bases sur les sous-methodes (O R G An B Au)

import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { cn } from '../lib/utils'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Card, CardContent } from '../components/ui'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  QuestionWizard,
  QuestionnaireIntro,
  type ReponseValeurV4,
} from '../components/diagnostic'
import { api } from '../services/api'

interface QuestionData {
  id: string
  code: string
  texte: string
  libelle?: string // API Prisma retourne 'libelle', JSON offline retourne 'texte'
  aide?: string // Mots-cles d'aide contextuelle
  methode: string // ORGA ou BILAN
  sousMethode: string
  sousMethodeLabel: string
  theme: string | { nom?: string } // Relation Theme (axes ORGA/BILAN)
  themeQuestion?: string // Theme detaille (ex: "evaluation des risques (DUER)")
  ordre: number
}

interface SelectedMetier {
  code: string
  nom: string
  ordre: number
}

interface LocationState {
  metiersSelectionnes?: SelectedMetier[]
  metierIds?: string[]
  completedMetierIndex?: number
  _navTimestamp?: number // Timestamp pour detecter les navigations sur meme route
}

// Themes Q2 bases sur les sous-methodes (comme HALTES)
// O = Orientations, R = Roles, G = Gestion, A = Animation, B = Bilans, Au = Audits
interface ThemeQ2 {
  code: string
  nom: string
  sousMethodePrefix: string // Pour matcher les questions
}

const THEMES_Q2: ThemeQ2[] = [
  { code: 'O', nom: 'Orientations', sousMethodePrefix: 'O_' },
  { code: 'R', nom: 'Roles et missions', sousMethodePrefix: 'R_' },
  { code: 'G', nom: 'Gestion des moyens', sousMethodePrefix: 'G_' },
  { code: 'An', nom: 'Animation', sousMethodePrefix: 'A-' },
  { code: 'B', nom: 'Bilans', sousMethodePrefix: 'B-' },
  { code: 'Au', nom: 'Audits', sousMethodePrefix: 'A_' },
]

export default function QuestionnaireQ2Page() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Charger les donnees du diagnostic (depuis state ou API)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  // Parametres specifiques a Q2
  const locationState = location.state as LocationState | undefined
  const completedMetierIndex = locationState?.completedMetierIndex ?? 0

  // Determiner s'il reste des metiers a completer
  const hasMoreMetiers = completedMetierIndex < metierIds.length - 1

  // Etat du questionnaire
  const [questions, setQuestions] = useState<QuestionData[]>([])
  const [reponses, setReponses] = useState<Map<string, { valeur?: number; commentaire?: string }>>(new Map())
  const reponsesRef = useRef(reponses)
  reponsesRef.current = reponses
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Navigation : theme courant et question courante
  const [currentThemeIndex, setCurrentThemeIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isReviewing, setIsReviewing] = useState(false)
  const [showIntro, setShowIntro] = useState(true)
  const [isRenewal, setIsRenewal] = useState(false)
  const [duerpWarning, setDuerpWarning] = useState(false)
  const hasAutoPositioned = useRef(false)

  // Effet dedie pour les navigations via stepper (se declenche a chaque changement de _navTimestamp)
  useEffect(() => {
    const state = location.state as LocationState
    if (state?._navTimestamp) {
      setCurrentThemeIndex(0)
      setCurrentQuestionIndex(0)
      // Si le questionnaire est deja complete, activer le mode revision pour afficher le wizard
      setIsReviewing(true)
      setShowIntro(false)
    }
  }, [(location.state as LocationState)?._navTimestamp])

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Generer les etapes de progression avec info multi-metiers
  const steps = useDiagnosticSteps('questionnaire-q2', diagnosticId, {
    totalMetiers: metierIds.length,
    currentMetierIndex: completedMetierIndex,
    q2Completed: false, // On est en train de le remplir
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Charger les questions et reponses
  useEffect(() => {
    async function loadData() {
      setIsLoading(true)

      try {
        // Charger les questions Q2 depuis l'API
        const questionsData: QuestionData[] = await api.getQuestionsQ2()

        // Trier par ordre
        questionsData.sort((a, b) => a.ordre - b.ordre)
        setQuestions(questionsData)

        // Charger les reponses existantes depuis l'API
        if (diagnosticId) {
          const diagnostic = await api.getDiagnostic(diagnosticId)
          if (diagnostic?.parentId) setIsRenewal(true)
          const existingReponses: any[] = diagnostic?.reponses || []
          const reponsesMap = new Map<string, { valeur: number; commentaire?: string }>()

          // Q2 n'a pas de metierId (commun a tous les metiers)
          existingReponses
            .filter((r: any) => !r.metierId)
            .forEach((r: any) => {
              reponsesMap.set(r.questionId, {
                valeur: r.valeur,
                commentaire: r.commentaire,
              })
            })

          setReponses(reponsesMap)
          if (reponsesMap.size > 0) setShowIntro(false)

          // Verifier si DUERP (Q2-03) est deja a "Non prevu" (0)
          const duerpQuestion = questionsData.find(q => q.code === 'Q2-03')
          if (duerpQuestion) {
            const duerpReponse = reponsesMap.get(duerpQuestion.id)
            if (duerpReponse?.valeur === 0) setDuerpWarning(true)
          }
        }
      } catch (error) {
        console.error('Erreur chargement questions Q2:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Auto-positionnement : après chargement, se placer sur la première question non-répondue
  useEffect(() => {
    if (isLoading || isLoadingData) return
    if (hasAutoPositioned.current) return
    if (locationState?._navTimestamp) return
    if (reponses.size === 0) return

    hasAutoPositioned.current = true

    // Construire les thèmes avec questions localement
    const grouped = new Map<string, QuestionData[]>()
    questions.forEach((q) => {
      const theme = THEMES_Q2.find(t => q.sousMethode.startsWith(t.sousMethodePrefix))
      if (theme) {
        if (!grouped.has(theme.code)) grouped.set(theme.code, [])
        grouped.get(theme.code)!.push(q)
      }
    })

    const themes = THEMES_Q2
      .filter(t => (grouped.get(t.code) || []).length > 0)
      .map(t => ({ ...t, questions: grouped.get(t.code) || [] }))

    // Trouver la première question non-répondue
    for (let ti = 0; ti < themes.length; ti++) {
      const theme = themes[ti]
      for (let qi = 0; qi < theme.questions.length; qi++) {
        const q = theme.questions[qi]
        if (reponses.get(q.id)?.valeur === undefined) {
          setCurrentThemeIndex(ti)
          setCurrentQuestionIndex(qi)
          setShowIntro(false)
          return
        }
      }
    }

    // Toutes répondues — si c'est un renouvellement, démarrer en mode review à la question 1
    if (isRenewal) {
      setCurrentThemeIndex(0)
      setCurrentQuestionIndex(0)
      setIsReviewing(true)
    }
    setShowIntro(false)
  }, [isLoading, isLoadingData, questions, reponses, locationState, isRenewal])

  // Grouper les questions par theme (sous-methode)
  const questionsByTheme = useMemo(() => {
    const grouped = new Map<string, QuestionData[]>()

    questions.forEach((q) => {
      // Trouver le theme correspondant a la sous-methode
      const theme = THEMES_Q2.find(t => q.sousMethode.startsWith(t.sousMethodePrefix))
      if (theme) {
        if (!grouped.has(theme.code)) {
          grouped.set(theme.code, [])
        }
        grouped.get(theme.code)!.push(q)
      }
    })

    return grouped
  }, [questions])

  // Liste des themes avec leurs questions (filtre les vides)
  const themesWithQuestions = useMemo(() => {
    return THEMES_Q2
      .filter((t) => {
        const themeQuestions = questionsByTheme.get(t.code) || []
        return themeQuestions.length > 0
      })
      .map((t) => ({
        ...t,
        questions: questionsByTheme.get(t.code) || [],
      }))
  }, [questionsByTheme])

  // Theme courant
  const currentTheme = themesWithQuestions[currentThemeIndex]

  // Questions du theme courant
  const currentQuestions = useMemo(() => {
    if (!currentTheme) return []
    return currentTheme.questions.map((q) => {
      const themeStr = q.themeQuestion
        || (typeof q.theme === 'string' ? q.theme : '')
      return {
        id: q.id,
        code: q.code,
        libelle: q.libelle || q.texte,
        aide: q.aide,
        theme: themeStr,
        ordre: q.ordre,
      }
    })
  }, [currentTheme])

  // Reponses du theme courant (pas de metierId pour Q2)
  const currentReponses = useMemo(() => {
    const filtered = new Map<string, { valeur?: number; commentaire?: string }>()
    if (!currentTheme) return filtered

    currentTheme.questions.forEach((q) => {
      const rep = reponses.get(q.id)
      if (rep) {
        filtered.set(q.id, rep)
      }
    })

    return filtered
  }, [currentTheme, reponses])

  // Calculer la progression par theme
  const themeProgress = useMemo(() => {
    return themesWithQuestions.map((theme) => {
      const answered = theme.questions.filter((q) => reponses.get(q.id)?.valeur !== undefined).length
      return {
        code: theme.code,
        answered,
        total: theme.questions.length,
      }
    })
  }, [themesWithQuestions, reponses])

  // Calculer la progression totale
  const totalAnswered = useMemo(() => {
    return themeProgress.reduce((acc, t) => acc + t.answered, 0)
  }, [themeProgress])

  const totalQuestions = useMemo(() => {
    return themeProgress.reduce((acc, t) => acc + t.total, 0)
  }, [themeProgress])

  const progressPercent = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0

  // Progression dans le thème courant
  const currentThemeProgress = themeProgress.find(t => t.code === currentTheme?.code)
  const themePercent = currentThemeProgress && currentThemeProgress.total > 0
    ? Math.round(((currentQuestionIndex + 1) / currentThemeProgress.total) * 100)
    : 0

  // Sauvegarder une reponse
  const handleReponseChange = useCallback(
    async (questionId: string, valeur: ReponseValeurV4) => {
      if (!diagnosticId) return

      // Avertissement DUERP : si Q2-03 = "Non prévu" (0), l'attestation sera bloquée
      const question = questions.find(q => q.id === questionId)
      if (question?.code === 'Q2-03') {
        setDuerpWarning(valeur === 0)
      }

      const existingComment = reponsesRef.current.get(questionId)?.commentaire

      // Mise a jour immediate UI
      setReponses((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(questionId)
        newMap.set(questionId, { ...existing, valeur })
        return newMap
      })

      // Sauvegarder directement via l'API (sans metierId pour Q2)
      try {
        await api.saveReponses(diagnosticId, {
          reponses: [{
            questionId,
            valeur,
            ...(existingComment ? { commentaire: existingComment } : {}),
          }],
        })
      } catch (error) {
        console.error('Erreur sauvegarde reponse:', error)
      }
    },
    [diagnosticId]
  )

  // Sauvegarder un commentaire
  const handleCommentChange = useCallback(
    async (questionId: string, commentaire: string) => {
      if (!diagnosticId) return

      setReponses((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(questionId)
        newMap.set(questionId, { ...(existing || {}), commentaire })
        return newMap
      })

      const existing = reponses.get(questionId)
      if (existing?.valeur !== undefined) {
        try {
          await api.saveReponses(diagnosticId, {
            reponses: [{
              questionId,
              valeur: existing.valeur,
              commentaire,
            }],
          })
        } catch (error) {
          console.error('Erreur sauvegarde commentaire:', error)
        }
      }
    },
    [diagnosticId, reponses]
  )

  // Navigation entre questions
  const handleNavigateQuestion = useCallback((index: number) => {
    setCurrentQuestionIndex(index)
  }, [])

  // Revenir au theme precedent (derniere question)
  const handleThemePrevious = useCallback(() => {
    if (currentThemeIndex > 0) {
      const prevThemeIndex = currentThemeIndex - 1
      const prevTheme = themesWithQuestions[prevThemeIndex]
      setCurrentThemeIndex(prevThemeIndex)
      setCurrentQuestionIndex(prevTheme.questions.length - 1)
    }
  }, [currentThemeIndex, themesWithQuestions])

  // Passer au theme suivant
  const handleThemeComplete = useCallback(() => {
    const nextThemeIndex = currentThemeIndex + 1
    if (nextThemeIndex < themesWithQuestions.length) {
      setCurrentThemeIndex(nextThemeIndex)
      setCurrentQuestionIndex(0)
    }
  }, [currentThemeIndex, themesWithQuestions.length])

  // Changer de theme via les tabs
  const handleThemeClick = useCallback((themeCode: string) => {
    const idx = themesWithQuestions.findIndex((t) => t.code === themeCode)
    if (idx !== -1) {
      setCurrentThemeIndex(idx)
      setCurrentQuestionIndex(0)
    }
  }, [themesWithQuestions])

  // Continuer vers resultats
  const handleNext = useCallback(async () => {
    if (!diagnosticId) return
    trackEvent('valider questionnaire q2')

    setIsSaving(true)

    try {
      if (hasMoreMetiers) {
        // Il reste des metiers a completer -> Synthese partielle
        navigate(`/diagnostic/${diagnosticId}/synthese`, {
          state: {
            metiersSelectionnes,
            metierIds,
            completedMetierIndex,
            isPartial: true,
          },
        })
      } else {
        // Tous les metiers sont completes -> Resultats finaux
        await api.terminerDiagnostic(diagnosticId)

        navigate(`/diagnostic/${diagnosticId}/resultats`, {
          state: { metiersSelectionnes, metierIds },
        })
      }
    } catch (error) {
      console.error('Erreur finalisation:', error)
      alert('Une erreur est survenue lors de la finalisation.')
    } finally {
      setIsSaving(false)
    }
  }, [diagnosticId, navigate, metiersSelectionnes, metierIds, completedMetierIndex, hasMoreMetiers])

  // Retour
  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/q1`, {
      state: locationState,
    })
  }

  // Determiner le label du bouton suivant
  const getNextLabel = () => {
    if (isSaving) return 'Sauvegarde...'
    if (hasMoreMetiers) return 'Voir la synthèse'
    return 'Voir les résultats'
  }

  // Ecran de chargement
  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement du questionnaire...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={handleNext}
      backLabel="Questionnaire Risques"
      nextLabel={getNextLabel()}
      nextDisabled={isSaving || progressPercent < 100}
      diagnosticState={diagnosticState}
    >
      {/* Layout focus — full height */}
      <div className="flex-1 flex flex-col min-h-0">
        {!showIntro && (<>
        {/* Barre unifiée : tabs thèmes + contexte + progression */}
        <div className="flex items-center gap-3 py-2 mb-2">
          {/* Tabs thèmes O R G An B Au */}
          <div className="flex gap-1 overflow-x-auto flex-shrink-0">
            {themesWithQuestions.map((theme) => {
              const progress = themeProgress.find((t) => t.code === theme.code)
              const isActive = theme.code === currentTheme?.code
              const isComplete = progress?.answered === progress?.total && (progress?.total ?? 0) > 0

              return (
                <button
                  key={theme.code}
                  onClick={() => handleThemeClick(theme.code)}
                  className={cn(
                    "flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {theme.code}
                </button>
              )
            })}
          </div>

          <div className="w-px h-5 bg-border flex-shrink-0" />

          {/* Contexte */}
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {currentTheme?.nom} · Question <span className="font-semibold text-foreground">{currentQuestionIndex + 1}</span>/{currentQuestions.length}
          </span>

          <div className="flex-1" />

          {/* Métier(s) — story 15. Q2 = commun à tous les métiers du diagnostic. */}
          {metiersSelectionnes.length > 0 && (
            <div className="flex items-center gap-1.5 flex-shrink-0 max-w-[60%] overflow-hidden">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Pour :</span>
              {metiersSelectionnes.map((metier) => (
                <Badge
                  key={metier.code}
                  variant="outline"
                  className="text-xs border-primary/30 bg-primary/5 text-primary max-w-[160px] truncate"
                >
                  <span className="truncate">{metier.nom}</span>
                </Badge>
              ))}
            </div>
          )}

          {/* Badge % global */}
          <Badge variant={progressPercent === 100 ? "default" : "secondary"} className="text-xs">
            {progressPercent}%
          </Badge>
        </div>

        {/* Barre fine progression thème courant */}
        <div className="h-1 bg-muted rounded-full mb-4">
          <div
            className="h-full bg-primary transition-all duration-300 rounded-full"
            style={{ width: `${themePercent}%` }}
          />
        </div>

        </>)}

        {/* Zone principale — flex-1 pour remplir l'espace */}
        {showIntro ? (
          <QuestionnaireIntro
            title="Questionnaire Management"
            subtitle={<>Ce questionnaire évalue l'organisation de la prévention dans votre entreprise selon 6 domaines : <span className="text-primary font-semibold">O</span>rientations, <span className="text-primary font-semibold">R</span>ôles &amp; missions, <span className="text-primary font-semibold">G</span>estion des moyens, <span className="text-primary font-semibold">A</span>nimation, <span className="text-primary font-semibold">B</span>ilans chantiers, <span className="text-primary font-semibold">A</span>udits &amp; visites.</>}
            communLabel="Commun à tous vos métiers"
            questionCount={totalQuestions}
            onStart={() => setShowIntro(false)}
          />
        ) : progressPercent === 100 && !isReviewing ? (
          <Card className="overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-muted/50 border-b border-border">
              <Badge className="bg-primary hover:bg-primary text-primary-foreground">
                Questionnaire terminé
              </Badge>
            </div>

            {/* Contenu */}
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold mb-6">Bravo, vous avez terminé !</h2>

              <div className="flex justify-center gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{totalAnswered}</div>
                  <div className="text-sm text-muted-foreground">questions</div>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{themesWithQuestions.length}</div>
                  <div className="text-sm text-muted-foreground">thèmes complétés</div>
                </div>
              </div>

              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Vous avez répondu à toutes les questions du questionnaire Management.
                Cliquez sur « {getNextLabel()} » pour continuer.
              </p>

              <div className="pt-6 border-t border-border flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    const firstTheme = themesWithQuestions[0]
                    if (firstTheme) {
                      setCurrentThemeIndex(0)
                      setCurrentQuestionIndex(0)
                      setIsReviewing(true)
                    }
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Vous avez un doute ? Revoir mes réponses
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          currentTheme && currentQuestions.length > 0 && (
            <>
              {duerpWarning && (
                <div className="mb-4 p-4 bg-destructive/10 border border-destructive/30 rounded-xl text-sm text-destructive">
                  <strong>Attention :</strong> Le DUERP n'est pas au minimum planifié. Vous ne pourrez pas générer d'attestation à l'issue du diagnostic.
                </div>
              )}
              <QuestionWizard
                key={`q2-${currentTheme.code}`}
                questions={currentQuestions}
                currentIndex={currentQuestionIndex}
                reponses={currentReponses}
                onReponseChange={handleReponseChange}
                onCommentChange={handleCommentChange}
                onNavigate={handleNavigateQuestion}
                onThemeComplete={handleThemeComplete}
                onThemePrevious={currentThemeIndex === 0 ? undefined : handleThemePrevious}
                themeName={currentTheme.nom}
              />
            </>
          )
        )}
      </div>
    </DiagnosticLayout>
  )
}
